import React, { useEffect, useRef, useState } from "react"
import _ from "lodash"
// PR
import { DataTable } from "primereact/datatable"
import { Column } from "primereact/column"
// Components
import { field as _field } from "components/table/lib/column-field"
import SearchHeader from "components/table/search-header"
import CarouselAutomation from "components/carousel-automation"
import {
  TableAutomation } from "components/table/table-automation"
import { NumberEdit, CheckboxInput, DropdownEdit } from "components/table/inputs"
import NumberInput from "components/inputs/number"
// Controls
// Data
import recoveryPoolsService, { RecoveryPool } from "data/recovery-pools"
import notificationService from "data/notification"
import { field_attrs } from "components/table/lib/column-field"
// Styles
import { InputText_s } from "styles/input-styles"
import { RecoveryPoolsDetailTable_s, FormRow_s } from "components/recoveries/recovery-pools/detail/recovery-pools-detail-styles"

type IRecoveryPoolLineItem = {
  lineItemId: number
  percent: number
  grossUpPercent: number
  adminFee: boolean
  subjectToCap: boolean
}

type ITenantContributionLineItem = {
  tenantContributionNamedItemId: number
  percent: number
}

type IRecoveryPool = {
  propertyid?: number
  recoverypoolid: number,
  recoverypooldetail: {
    expenseItems: {
      poolAdminFee: number | null,
      lineItems: IRecoveryPoolLineItem[]
    },
    tenantContributionItems: {
      items: ITenantContributionLineItem[]
    }
  }
}

type ILineItem = {
  lineitemid: number
  code: string
  name: string
}

type ITenantContribution = {
  tenantcontributionid: number
  code: string
  name: string
}

type IRecoveryPoolsDetailTable = {
  tableId: string
  recoveryPool: IRecoveryPool
  lineItems: ILineItem[]
  tenantContributions: ITenantContribution[]
  hasSelectedItems: boolean
  property: {}
}

type ContextRow = {
  recoverypoolid: number
  propertyid: number
  code: string
  systeminput?: string
}

type INormalizedPool = {
  itemId: number
  itemName: string
  selected: boolean
  percent: number | null
  grossUpPercent: number | null
  adminFee: boolean | null
  subjectToCap: boolean | null
  type: string
  displayType: string
}

const RecoveryPoolsDetailTable = (props: IRecoveryPoolsDetailTable) => {
  const usePrevious = (value) =>{
    const ref = useRef();
    useEffect(() => {
      ref.current = value;
    });
    return ref.current;
  }

  const [searchFilter, setSearchFilter] = useState<string>("")
  const [displayAll, setDisplayAll] = useState<boolean>(true)
  const [selectedRecoveryPoolType, setSelectedRecoveryPoolType] = useState<string>("All")
  const [fee, setFee] = useState<number | null>(props?.recoveryPool?.recoverypooldetail?.expenseItems?.poolAdminFee)
  const [automationActive, setAutomationActive] = useState<boolean>(false);
  const [tableAutomationActive, setTableAutomationActive] = useState<boolean>(true);
  const poolTypes = [{text: "All", value:"all"}, {text:"Expense", value: "expense"}, {text: "Tenant Contrib", value: "tenant_contrib"}]
  const prevSearch = usePrevious(searchFilter);
  const tableRef = useRef() as React.MutableRefObject<DataTable>
  const field = _field('recoverypool')

  useEffect(() => {
    if (prevSearch !== "" && searchFilter === "") _.invoke(tableRef, 'current.filter', '', 'contains');
    else if (searchFilter !== "") _.invoke(tableRef, 'current.filter', searchFilter, 'contains');
    else return
  }, [searchFilter])

  useEffect(() => {
    setFee(props?.recoveryPool?.recoverypooldetail?.expenseItems?.poolAdminFee)
  }, [props.recoveryPool, props.lineItems, props.tenantContributions])

  useEffect(() => {
    setDisplayAll(!props.hasSelectedItems)
  }, [props.hasSelectedItems])

  const rowClassName = (rowData) => {
    if (rowData.systeminput && rowData.systeminput === 'system'){
      return {'system-row' : rowData.systeminput === 'system'}
    }
    else if (rowData.systeminput && rowData.systeminput === 'systemImmutable'){
      return {'systemImmutable-row' : rowData.systeminput === 'systemImmutable'}
    }
    else return {'' : rowData}
  }

  // Centralize the logic to update recovery pool items.
  // This takes the state and resolve method from the columns editor update callback.
  // This method encapsulates the logic for when a field can be updated based on
  // the field type or on the state of another field in the same row, as well
  // as de-normalizing the row data back into the required shape for storage in postgres.
  const persist = (value, field, rowData) => {
    let noOp = false;
    let payload: IRecoveryPool = _.cloneDeep(props.recoveryPool)
    if(!payload.recoverypooldetail) _.set(payload, "recoverypooldetail", {})

    // here is where the payload is updated for any changed table data
    if (rowData) {
      if(!payload.recoverypooldetail.expenseItems) payload.recoverypooldetail.expenseItems = {lineItems: [], poolAdminFee: null}
      payload.recoverypooldetail.expenseItems.poolAdminFee = fee

      if (rowData.type === 'expense') {
        switch (field) {
          case 'percent':
            addOrRemoveFromPayload(payload, field, value, rowData);
            break;
          default: // all other fields are dependent on the value of percent
            if (rowData.percent) {
              addOrRemoveFromPayload(payload, field, value, rowData);
            } else {
              noOp = true;
              notificationService.notify({
                message: 'Please first fill out the percent field',
                type: 'warn',
                dismiss: 2000
              })
            }
        }
      } else {
        switch (field) {
          case 'percent':
            addOrRemoveFromPayload(payload, field, value, rowData);
            break;
          default: // tenant contributions can only edit the percent field
            noOp = true;
            notificationService.notify({
              message: 'This field is not valid for tenant contributions',
              type: 'warn',
              dismiss: 2000
            })
        }
      }
      if (!noOp) {
        if (payload?.recoverypooldetail?.tenantContributionItems && !payload?.recoverypooldetail?.tenantContributionItems?.items?.length) {
          // @ts-ignore TS2790
          delete payload.recoverypooldetail.tenantContributionItems;
        }
        recoveryPoolsService.patch(payload as RecoveryPool);
      }
    } else {
      if (payload?.recoverypooldetail?.expenseItems?.poolAdminFee !== fee) {
        // build updated recovery pool payload
        if(!payload.recoverypooldetail.expenseItems) payload.recoverypooldetail.expenseItems = {lineItems: [], poolAdminFee: null}
        payload.recoverypooldetail.expenseItems.poolAdminFee = fee
        if (!noOp) {
          if (payload?.recoverypooldetail?.tenantContributionItems && !payload?.recoverypooldetail?.tenantContributionItems?.items?.length) {
            // @ts-ignore TS2790
            delete payload.recoverypooldetail.tenantContributionItems;
          }
          recoveryPoolsService.patch(payload as RecoveryPool);
        }
      }
    }
  }

  // Helper for updateRecoveryPool which either adds a denormalized item to the payload,
  // updates an existing item in the payload, or removes an item from the payload.
  // Allows the parent function to focus on implementing the field dependency rules without
  // doing too much data manipulation.  This operates on the payload in-place (mutable).
  // The add vs remove operation is determined by whether the field has a value or not.
  const addOrRemoveFromPayload = (payload, field, value, row) => {
    const rowId = row.itemId;
    const rowType = row.type;
    if (rowType === 'expense') {
      // check if row exists
      if(!payload.recoverypooldetail.expenseItems) payload.recoverypooldetail.expenseItems = {lineItems: [], poolAdminFee: null}
      const itemIndex = payload.recoverypooldetail.expenseItems.lineItems.findIndex(item => item.lineItemId === rowId);
      if((value === 0 || !value) && itemIndex === -1) return // do nothing since they dont want to persist 0's (they want to delete row on 0)
      if (value) {
        if (itemIndex !== -1) {
          payload.recoverypooldetail.expenseItems.lineItems[itemIndex][field] = value;
        } else { // if it doesnt exist, create a new row and append it to the array of selected items
          let rowCopy = Object.assign({}, row);
          delete rowCopy.itemId;
          delete rowCopy.itemName;
          delete rowCopy.selected;
          delete rowCopy.type;
          delete rowCopy.displayType;
          delete rowCopy.errors;
          rowCopy.lineItemId = row.itemId;
          rowCopy[field] = value;
          rowCopy.adminFee = true;
          rowCopy.subjectToCap = true;
          payload.recoverypooldetail.expenseItems.lineItems.push(rowCopy);
        }
      } else {
        if (field === 'percent') { // if percent is removed, remove the whole row
          payload.recoverypooldetail.expenseItems.lineItems.splice(itemIndex, 1);
        } else { // otherwise just null out that field
          if (field === 'adminFee' || field === 'subjectToCap') {
            payload.recoverypooldetail.expenseItems.lineItems[itemIndex][field] = false;
          } else {
            payload.recoverypooldetail.expenseItems.lineItems[itemIndex][field] = null;
          }
        }
      }
    } else { // tenant contributions
      // check if row exists
      if(!payload.recoverypooldetail.tenantContributionItems) payload.recoverypooldetail.tenantContributionItems = {items: []}
      const itemIndex = payload.recoverypooldetail.tenantContributionItems.items.findIndex(item => item.tenantContributionNamedItemId === rowId);
      if(value === 0 && itemIndex === -1) return // do nothing since they dont want to persist 0's (they want to delete row on 0)
      if (field === 'percent') { // tenantContribution can only edit percent field
        if (value) {
          if (itemIndex !== -1) {
            payload.recoverypooldetail.tenantContributionItems.items[itemIndex]['percent'] = value;
          } else { // if it doesnt exist, append it to the array of selected items
            const newRow = {
              tenantContributionNamedItemId: rowId,
              percent: value
            }
            payload.recoverypooldetail.tenantContributionItems.items.push(newRow);
          }
        } else {
          payload.recoverypooldetail.tenantContributionItems.items.splice(itemIndex, 1);
        }
      }
    }
  }

  let allVisibleRowsSelected = true
  // Given all recovery pool expenses and all tenant contributions
  // as well as the selected expenses and selected contributions
  // this function will output the data scruture that matches the column
  // configuration above with metadata added for filtering purposes.
  const normalizeRecoveryPools = (): INormalizedPool[] => {
    let tableRows: INormalizedPool[] = []
    const pool = Object.assign(props.recoveryPool, {})
    const recoverypooldetail = pool.recoverypooldetail || {}
    const expenseItems = recoverypooldetail.expenseItems || {}
    const tenantContributionItems = recoverypooldetail.tenantContributionItems || {}
    const allExpenses = props.lineItems || []
    const allTenantContributions = props.tenantContributions || []
    const selectedExpenses = expenseItems.lineItems || []
    const selectedTenantContributions = tenantContributionItems.items || []

    if (allExpenses.length || allTenantContributions.length) {
      // normalize the expense recovery pools
      const normalizedExpensePools = allExpenses.map(item => {
        // determine if the current recovery pool has already been selected
        const selected = selectedExpenses.find(selected => selected.lineItemId === item.lineitemid);
        const ix = _.findIndex(expenseItems.lineItems, it => it.lineItemId == item.lineitemid)

        return {
          itemId: item.lineitemid,
          itemName: `Expense: ${item.code} - ${item.name?.slice(0, 26) ?? ''}`,
          selected: selected ? true : false,
          percent: selected ? selected.percent : null,
          grossUpPercent: selected ? selected.grossUpPercent : null,
          adminFee: selected ? selected.adminFee : null,
          subjectToCap: selected ? selected.subjectToCap : null,
          type: 'expense',
          displayType: 'Expense',
          // errors: _(errors)
          //   .filter(it => _.isEqual(it.path.slice(1, -1), ['expenseItems', 'lineItems', ix]))
          //   .transform((errors, it) => {
          //     errors[_.last(it.path)] = (errors[_.last(it.path)] || []).concat(it)
          //   }, {})
          //   .value()
        };
      });
      // normalize the tenant contribution pools
      const normalizedTenantPools = allTenantContributions.map(item => {
        // determine if the current recovery pool has already been selected
        const selected = selectedTenantContributions.find(selected => selected.tenantContributionNamedItemId === item.tenantcontributionid);
        const ix = _.findIndex(tenantContributionItems.items, it => it.tenantContributionNamedItemId == item.tenantcontributionid)

        return {
          itemId: item.tenantcontributionid,
          itemName: `Tenant Contrib: ${item.code} - ${item.name?.slice(0, 26) ?? ''}`,
          selected: selected ? true : false,
          percent: selected ? selected.percent : null,
          grossUpPercent: null,
          adminFee: null,
          subjectToCap: null,
          type: 'tenant_contrib',
          displayType: 'Tenant Contrib',
          // errors: _(errors)
          //   .filter(it => _.isEqual(it.path.slice(1, -1), ['tenantContributionItems', 'items', ix]))
          //   .transform((errors, it) => {
          //     errors[_.last(it.path)] = (errors[_.last(it.path)] || []).concat(it)
          //   }, {})
          //   .value()

        };
      });
      // concatenate the two normalized arrays
      tableRows = normalizedExpensePools.concat(normalizedTenantPools);
    }
    // filter recovery pools based on the currently selected recovery pool type if 'all' is not selected
    if (selectedRecoveryPoolType !== "All") {
      tableRows = tableRows.filter(item => _.get(_.find(poolTypes, {value: item.type}), "text") === selectedRecoveryPoolType);
    }
    // filter recovery pools based on the current search term
    if (searchFilter) {
      tableRows = tableRows.filter(item => item.itemName.toLowerCase().includes(searchFilter.toLowerCase()));
    }
    // filter recovery pools based on show all or selected
    if (!displayAll) {
      tableRows = tableRows.filter(item => item.selected === true);
    }

    allVisibleRowsSelected = _.reduce(tableRows, (allSelected, row) => {
      if(!allSelected) return false
      else if(!row.selected) return false
      else return true
    }, true)

    return tableRows;
  }

  const tableData = normalizeRecoveryPools()

  const feeChangeHandler = (field: string, val:number) => {
    let payload: IRecoveryPool = _.cloneDeep(props.recoveryPool)
    if(!payload.recoverypooldetail) _.set(payload, "recoverypooldetail", {})
    if(!payload.recoverypooldetail.expenseItems) payload.recoverypooldetail.expenseItems = {lineItems: [], poolAdminFee: null}
    payload.recoverypooldetail.expenseItems.poolAdminFee = val

    recoveryPoolsService.patch(payload as RecoveryPool)
    setFee(val)
  }

  const rowSelectionHandler = (type: "ALL" | "SINGLE", item?: INormalizedPool) => {
    let payload: IRecoveryPool = _.cloneDeep(props.recoveryPool)
    if(!payload.recoverypooldetail) _.set(payload, "recoverypooldetail", {})

    let visibleRows = tableData

    if(type === "ALL") {
      if(visibleRows.length) {
        if(allVisibleRowsSelected) {
          _.each(visibleRows, (row) => {
            addOrRemoveFromPayload(payload, "percent", 0, row)
          })
        } else {
          _.each(visibleRows, (row) => {
            addOrRemoveFromPayload(payload, "percent", 100, row)
          })
        }
      }
    } else {
      if(item && item.selected) {
        addOrRemoveFromPayload(payload, "percent", 0, item)
      } else {
        addOrRemoveFromPayload(payload, "percent", 100, item)
      }
    }

    recoveryPoolsService.patch(payload as RecoveryPool)
  }

  const item_attrs = (field) => field_attrs('recoverypool', 'recoverypooldetail.expenseItems')(field)(props.recoveryPool, props)

  return (
    <div>
      <SearchHeader setSearchFilter={setSearchFilter} />
      <FormRow_s>
        <div>
          <label>Display</label>
          <div className="percent-line-radio-buttons">
            <label>
              <input
                type="radio"
                name="state"
                value="all"
                onChange={() => setDisplayAll(true)}
                className="automated"
                checked={displayAll === true}/>
              All
            </label>
            <label>
              <input
                type="radio"
                name="state"
                value="selected"
                onChange={() => setDisplayAll(false)}
                className="automated"
                checked={displayAll == false}/>
              Selected
            </label>
          </div>
        </div>
        <div>
          <label>Pool Admin Fee %</label>
          <NumberInput field="poolAdminFee" value={fee} persist={feeChangeHandler} readPrecision={4} writePrecision={4} className="automated" attrs={item_attrs}/>
        </div>
      </FormRow_s>
      <RecoveryPoolsDetailTable_s id={`${props.tableId}-scrollable-container`}>
        { props.recoveryPool && <DataTable
          globalFilter={searchFilter}
          id={props.tableId}
          ref={tableRef}
          value={tableData}
          paginator={true}
          rows={20}
          rowsPerPageOptions={[10, 20, 50, 100, 500]}
          scrollable={true}
          paginatorPosition="bottom"
          rowClassName={rowClassName}
          stateStorage="custom"
          customSaveState={state => localStorage.setItem("primereact-table-preferences", JSON.stringify({rows: state.rows}))}
          customRestoreState={() => JSON.parse(localStorage.getItem("primereact-table-preferences")!)} >

          <Column style={{width: "100px"}} className="p-editable-column"
            sortable
            header="Selected"
            filter
            filterElement={
              <CheckboxInput field="allVisibleRowsSelected" rowData={{allVisibleRowsSelected}} persist={() => rowSelectionHandler("ALL")} />
            }
            {...field('selected', (rowData, props) => CheckboxInput({persist: () => rowSelectionHandler("SINGLE", _.get(props, "rowData")), ...props}))}
            editor={props => CheckboxInput({persist: () => rowSelectionHandler("SINGLE", _.get(props, "rowData")), ...props})} />
          <Column style={{width: "250px"}}
            sortable
            filter
            filterElement={
              <DropdownEdit
                field="selectedRecoveryPoolType"
                rowData={{selectedRecoveryPoolType}}
                options={["All", "Expense", "Tenant Contrib"]}
                persist={(val) => setSelectedRecoveryPoolType(val)} />
            }
            header="Type"
            {...field('displayType')}
          />
          <Column style={{minWidth: "350px", maxWidth: "350px"}}
            sortable
            filter
            filterElement={
              <InputText_s value={searchFilter} onChange={(e) => setSearchFilter(e.target.value)} />
            }
            header="Item"
            {...field('itemName')}
          />
          <Column style={{minWidth: "110px", maxWidth: "110px"}} className="p-editable-column"
            sortable
            // filter
            // filterMatchMode="contains"
            header="Percent"
            {...field('percent')}
            editor={props => NumberEdit({persist, writePrecision: 4, ...props})}
          />
          <Column style={{minWidth: "110px", maxWidth: "110px"}} className="p-editable-column"
            sortable
            // filter
            // filterMatchMode="contains"
            header="Gross Up %"
            // NB validation no longer works because the refactored tableData no longer has everything it needs to find/filter the errors, so by the time this callback happens, there are no errors to check.
            {...field('grossUpPercent', null, (e, rowData) => e.type == 'number.VariableExpense' && e['value'] == rowData.itemId, 'itemId')}
            editor={props => NumberEdit({persist, writePrecision: 4, ...props})}
          />
          <Column style={{minWidth: "110px", maxWidth: "110px"}} className="p-editable-column"
            sortable
            header="Cap?"
            // filter
            // filterElement={
            //   <CheckboxInput field="allCapToggle" rowData={{allCapToggle: _.find(_.filter(selectedRows, r => r.type === "expense"), r => r.subjectToCap === false) ? false : !_.get(_.filter(selectedRows, r => r.type === "expense"), "length") ? false : true}} persist={(val) => capSelectAllToggle(val)} />
            // }
            {...field('subjectToCap', (rowData, props) => CheckboxInput({persist, ...props}))}
            editor={props => CheckboxInput({persist, ...props})} />
          <Column style={{minWidth: "110px", maxWidth: "110px"}} className="p-editable-column"
            sortable
            header="Admin Fee?"
            // filter
            // filterElement={
            //   <CheckboxInput field="allAdminFeeToggle" rowData={{allAdminFeeToggle: _.find(_.filter(selectedRows, r => r.type === "expense"), r => r.adminFee === false) ? false : !_.get(_.filter(selectedRows, r => r.type === "expense"), "length") ? false : true}} persist={(val) => adminFeeSelectAllToggle(val)} />
            // }
            {...field('adminFee', (rowData, props) => CheckboxInput({persist, ...props}))}
            editor={props => CheckboxInput({persist, ...props})} />
        </DataTable> }
        { tableData.length
          ? <div>
              <CarouselAutomation outsideTable={true} automationActive={automationActive} editableFilters={true} includeSearchBar={true} tabOut={() => { setAutomationActive(false); setTableAutomationActive(true) }} />
              <TableAutomation tableId={props.tableId} automationActive={tableAutomationActive} shiftTabOut={() => setTableAutomationActive(false)}/>
            </div>
          : null
        }
      </RecoveryPoolsDetailTable_s>
    </div>
  )
}

export default RecoveryPoolsDetailTable
